import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { mobilePhoneWithArrow } from '@/assets/images/emojis';
import { routes } from '@/routes/routes';
import { ApiError } from '@/types';
import { useError } from '@/providers/Error';
import { bcPhoneMessages } from '@/constants/errorMessages';
import { useScoring } from '../../hooks/useScoring';
import {
  PhoneVerificationError,
  ScoringErrorCode,
  type BcPhoneNumber,
} from '../../types/scoring';
import styles from './BcPhoneSelectPage.module.scss';

export interface BcPhoneCodeState {
  phone: BcPhoneNumber;
}

export function BcPhoneSelectPage() {
  const navigate = useNavigate();
  const { setError } = useError();
  const {
    bcPhones,
    isBcPhonesLoading,
    sendVerificationCode,
    isSendingCode,
    refreshScoringState,
  } = useScoring({ fetchBcPhones: true });
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>();

  if (isBcPhonesLoading) {
    return <PageLoader />;
  }

  const phones = bcPhones ?? [];
  const selectedPhone =
    phones.find((phone) => phone.id === selectedPhoneId) ?? phones[0];

  const handleSendCode = async () => {
    if (!selectedPhone) {
      return;
    }

    try {
      const attempt = await sendVerificationCode(selectedPhone.id);
      // A failed SMS still answers HTTP 200.
      if (attempt.phoneVerificationError !== PhoneVerificationError.None) {
        setError(bcPhoneMessages.sms_failed);
        return;
      }

      const state: BcPhoneCodeState = { phone: selectedPhone };
      navigate(routes.scoring.code, { state });
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.message === ScoringErrorCode.AlreadyHasScore
      ) {
        await refreshScoringState();
        navigate(routes.wizard.base);
        return;
      }

      setError(bcPhoneMessages.unexpected);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <img
            src={mobilePhoneWithArrow}
            alt="Mobile phone emoji"
            className={styles.emoji}
          />
          <h1 className={styles.title}>Validează-ți numărul</h1>
          <p className={styles.description}>
            {phones.length > 0
              ? 'Alege unul din numerele de telefon înregistrate la Biroul de Credit pentru validare prin SMS.'
              : 'Nu am găsit numere de telefon înregistrate la Biroul de Credit.'}
          </p>
        </div>

        <div className={styles.phoneList}>
          {phones.map((phone) => {
            const isSelected = phone.id === selectedPhone?.id;
            return (
              <button
                key={phone.id}
                type="button"
                className={clsx(styles.phoneOption, {
                  [styles['phoneOption--selected']]: isSelected,
                })}
                aria-pressed={isSelected}
                onClick={() => setSelectedPhoneId(phone.id)}
              >
                {phone.phone}
                {isSelected && <Icon name="check" />}
              </button>
            );
          })}
        </div>

        <p className={styles.otherMethod}>
          Nu apare numărul tău?{' '}
          <button
            type="button"
            className={styles.otherMethodLink}
            onClick={() => navigate(routes.kyc.base)}
          >
            Încearcă altă metodă
          </button>
        </p>
      </div>

      {selectedPhone && (
        <div className={styles.bottomSection}>
          <Button
            trackingId="send_bc_phone_code"
            text="Trimite SMS"
            rightIcon={<Icon name="arrow-right" />}
            onClick={handleSendCode}
            loading={isSendingCode}
            className={styles.sendButton}
          />
        </div>
      )}
    </div>
  );
}

export default BcPhoneSelectPage;
